/**
 * MatriceCompetencesPage.jsx
 *
 * Gestion des matrices de compétences : création, édition, lignes de matrice.
 * Utilise ContratMatrixManager (existant) + ArticleCatalogManager (existant).
 * Self-contained : fetch direct sans hook partagé.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ListAltIcon from '@mui/icons-material/ListAlt';

import ArticleCatalogManager from '../ArticleCatalogManager';
import ContratMatrixManager from '../ContratMatrixManager';
import { contratApi } from './ContratPageShared';

export default function ContratMatrixPage() {
  const [tab, setTab] = useState(0);
  const [matrices, setMatrices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loadingMatrices, setLoadingMatrices] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [error, setError] = useState('');

  // ── Fetches ────────────────────────────────────────────────────────────────

  const fetchMatrices = useCallback(async () => {
    setLoadingMatrices(true);
    try {
      const { data } = await contratApi.get('/api/contrat/matrix');
      setMatrices(Array.isArray(data) ? data : []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur chargement matrices');
    } finally {
      setLoadingMatrices(false);
    }
  }, []);

  const fetchArticles = useCallback(async () => {
    setLoadingArticles(true);
    try {
      const { data } = await contratApi.get('/api/contrat/articles/all');
      setArticles(Array.isArray(data) ? data : []);
    } catch {
      setArticles([]);
    } finally {
      setLoadingArticles(false);
    }
  }, []);

  useEffect(() => {
    fetchMatrices();
    fetchArticles();
  }, [fetchMatrices, fetchArticles]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const isLoading = loadingMatrices || loadingArticles;

  return (
    <Box>
      {/* ── En-tête ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Matrice de compétences</Typography>
          <Typography variant="caption" color="text.secondary">
            Gestion des matrices de compétences et du catalogue d'articles.
          </Typography>
        </Box>
        {isLoading && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Chargement…</Typography>
          </Stack>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Tabs ── */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<GridViewIcon fontSize="small" />}
            iconPosition="start"
            label={`Matrices (${matrices.length})`}
          />
          <Tab
            icon={<ListAltIcon fontSize="small" />}
            iconPosition="start"
            label={`Articles (${articles.length})`}
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tab === 0 && (
            <ContratMatrixManager
              inline
              matrices={matrices}
              api={contratApi}
              onChanged={fetchMatrices}
            />
          )}
          {tab === 1 && (
            <ArticleCatalogManager
              inline
              articles={articles}
              api={contratApi}
              onChanged={fetchArticles}
            />
          )}
        </Box>
      </Paper>

      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" color="text.secondary">
        {matrices.length} matrice(s) · {articles.length} article(s)
      </Typography>
    </Box>
  );
}